import React, { useRef, useState } from 'react';
import { Download, Maximize2, X, FileSpreadsheet, Image as ImageIcon } from 'lucide-react';

// Shared palette — aligned with the page's existing gradient colors + tokens.
export const PALETTE = {
  orange: '#f97316',
  green: '#10b981',
  blue: '#3b82f6',
  purple: '#a855f7',
  amber: '#f59e0b',
  red: '#ef4444',
  slate: '#64748b',
  teal: '#14b8a6',
};
export const SERIES = [PALETTE.blue, PALETTE.green, PALETTE.purple, PALETTE.amber, PALETTE.teal, PALETTE.red];

// ---- export helpers --------------------------------------------------------

export function downloadCsv(filename: string, rows: Array<Record<string, any>>): void {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const esc = (v: any) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`);
}

// Serialize the first <svg> inside a node to a PNG (no external deps).
export function exportSvgToPng(node: HTMLElement | null, filename: string): void {
  const svg = node?.querySelector('svg');
  if (!svg) return;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  const rect = svg.getBoundingClientRect();
  const w = Math.max(rect.width, 320);
  const h = Math.max(rect.height, 200);
  clone.setAttribute('width', String(w));
  clone.setAttribute('height', String(h));
  const xml = new XMLSerializer().serializeToString(clone);
  const svg64 = `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(xml)))}`;
  const img = new Image();
  img.onload = () => {
    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = w * scale;
    canvas.height = h * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(scale, scale);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (blob) triggerDownload(blob, filename.endsWith('.png') ? filename : `${filename}.png`);
    });
  };
  img.src = svg64;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---- Chart card ------------------------------------------------------------

interface ChartCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  loading?: boolean;
  empty?: boolean;
  emptyText?: string;
  /** rows for CSV export; when provided a CSV button appears */
  csv?: { filename: string; rows: Array<Record<string, any>> };
  /** enable PNG export (requires an <svg> in the body) */
  png?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const ChartCard: React.FC<ChartCardProps> = ({
  title, subtitle, icon, loading, empty, emptyText, csv, png, className, children,
}) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const Body = (
    <div className="s-admin-chartx-body" ref={bodyRef}>
      {loading ? (
        <ChartSkeleton />
      ) : empty ? (
        <EmptyState text={emptyText} />
      ) : (
        children
      )}
    </div>
  );

  return (
    <>
      <div className={`s-admin-chartx-card ${className || ''}`}>
        <div className="s-admin-chartx-header">
          <div className="s-admin-chartx-title">
            {icon && <span className="s-admin-chartx-icon">{icon}</span>}
            <div>
              <h3>{title}</h3>
              {subtitle && <p>{subtitle}</p>}
            </div>
          </div>
          <div className="s-admin-chartx-actions">
            {(csv || png) && (
              <div className="s-admin-chartx-menu-wrap">
                <button className="s-admin-chartx-btn" title="Export" onClick={() => setMenuOpen((v) => !v)}>
                  <Download size={16} />
                </button>
                {menuOpen && (
                  <>
                    <div className="s-admin-chartx-menu-backdrop" onClick={() => setMenuOpen(false)} />
                    <div className="s-admin-chartx-menu">
                      {csv && (
                        <button onClick={() => { downloadCsv(csv.filename, csv.rows); setMenuOpen(false); }}>
                          <FileSpreadsheet size={14} /> Export CSV
                        </button>
                      )}
                      {png && (
                        <button onClick={() => { exportSvgToPng(bodyRef.current, title.replace(/\s+/g, '-').toLowerCase()); setMenuOpen(false); }}>
                          <ImageIcon size={14} /> Export PNG
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            <button className="s-admin-chartx-btn" title="Expand" onClick={() => setExpanded(true)}>
              <Maximize2 size={16} />
            </button>
          </div>
        </div>
        {Body}
      </div>

      {expanded && (
        <div className="s-admin-chartx-overlay" onClick={() => setExpanded(false)}>
          <div className="s-admin-chartx-modal" onClick={(e) => e.stopPropagation()}>
            <div className="s-admin-chartx-header">
              <div className="s-admin-chartx-title">
                {icon && <span className="s-admin-chartx-icon">{icon}</span>}
                <div><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</div>
              </div>
              <button className="s-admin-chartx-btn" onClick={() => setExpanded(false)}><X size={18} /></button>
            </div>
            <div className="s-admin-chartx-modal-body">
              {loading ? <ChartSkeleton /> : empty ? <EmptyState text={emptyText} /> : children}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export const ChartSkeleton: React.FC = () => (
  <div className="s-admin-chartx-skeleton">
    <div className="s-admin-chartx-skeleton-bars">
      {[60, 85, 45, 95, 70, 55, 80].map((h, i) => (
        <div key={i} className="s-admin-chartx-skeleton-bar" style={{ height: `${h}%` }} />
      ))}
    </div>
  </div>
);

export const EmptyState: React.FC<{ text?: string }> = ({ text }) => (
  <div className="s-admin-chartx-empty">
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3v18h18" />
      <path d="M18 17V9M13 17V5M8 17v-3" />
    </svg>
    <span>{text || 'No data available yet'}</span>
  </div>
);

// ---- KPI stat card ---------------------------------------------------------

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  tone: 'blue' | 'green' | 'purple' | 'orange' | 'teal' | 'red';
  delta?: number | null; // percent change vs previous period
  sub?: string;
}

export const StatCard: React.FC<StatCardProps> = ({ label, value, icon, tone, delta, sub }) => {
  const showDelta = typeof delta === 'number' && isFinite(delta);
  const up = (delta || 0) >= 0;
  return (
    <div className={`s-admin-statx-card tone-${tone}`}>
      <div className="s-admin-statx-top">
        <span className={`s-admin-statx-icon tone-${tone}`}>{icon}</span>
        {showDelta && (
          <span className={`s-admin-statx-delta ${up ? 'up' : 'down'}`}>
            {up ? '▲' : '▼'} {Math.abs(delta as number)}%
          </span>
        )}
      </div>
      <div className="s-admin-statx-value">{value}</div>
      <div className="s-admin-statx-label">{label}</div>
      {sub && <div className="s-admin-statx-sub">{sub}</div>}
    </div>
  );
};

// ---- Insight callout -------------------------------------------------------

export interface Insight {
  tone: 'positive' | 'warning' | 'info';
  icon: React.ReactNode;
  text: string;
}

export const InsightStrip: React.FC<{ insights: Insight[] }> = ({ insights }) => {
  if (!insights.length) return null;
  return (
    <div className="s-admin-insightx-strip">
      {insights.map((ins, i) => (
        <div key={i} className={`s-admin-insightx tone-${ins.tone}`}>
          <span className="s-admin-insightx-icon">{ins.icon}</span>
          <span>{ins.text}</span>
        </div>
      ))}
    </div>
  );
};
